import { create } from "zustand";
import { combine } from "zustand/middleware";

type PriceMonitorSource = "item" | "favorites" | "product";

interface OpenPriceMonitorOptions {
  favoriteId?: number | null;
  source?: PriceMonitorSource;
}

/**
 * Initial state for the UI store.
 * Contains modal states, item selection, and data loading flags.
 */
const initialState = {
  /** Whether the favorites/collections modal is open */
  favoritesModalOpen: false,
  priceMonitorOpen: false,
  priceMonitorItem: null as SearchResultItem | null,
  priceMonitorFavoriteId: null as number | null,
  priceMonitorSource: null as PriceMonitorSource | null,
  /** Whether the filter pane is open */
  filterPaneOpen: false,
  /** Whether the item detail modal is open */
  itemModalOpen: false,
  adminItemModalOpen: false,
  /** The item to show in the slide-in panel */
  slideInItem: null as SearchResultItem | null,
  /** Whether user data (filters, collections, follows) has been loaded */
  userDataLoaded: false,
  /** Whether the KURBO no-tokens nag modal is open */
  kurboNoTokensNagOpen: false,
  /** Whether the "why is this flagged" warning explanation modal is open */
  warningModalOpen: false,
};

/**
 * UI store manages global UI state.
 *
 * Features:
 * - Modal open/close states (favorites, filters, item detail)
 * - Slide-in item for mobile/detail view
 * - User data loading state
 *
 * @example
 * ```ts
 * const { actions } = useUiStore();
 * actions.toggleFavoritesModal();
 * actions.setSlideInItem(item);
 * ```
 */
export const useUiStore = create(
  combine(initialState, (set, get) => ({
    actions: {
      /** Sets whether the favorites modal is open */
      setFavoritesModalOpen: (open: boolean) => set({ favoritesModalOpen: open }),
      openPriceMonitor: (item: SearchResultItem, options: OpenPriceMonitorOptions = {}) =>
        set({
          priceMonitorOpen: true,
          priceMonitorItem: item,
          priceMonitorFavoriteId: options.favoriteId ?? null,
          priceMonitorSource: options.source ?? null,
        }),
      setPriceMonitorFavoriteId: (favoriteId: number | null) =>
        set({
          priceMonitorFavoriteId: favoriteId,
        }),
      closePriceMonitor: () =>
        set({
          priceMonitorOpen: false,
          priceMonitorItem: null,
          priceMonitorFavoriteId: null,
          priceMonitorSource: null,
        }),

      /** Sets whether the filter pane is open */
      setFilterPaneOpen: (open: boolean) => set({ filterPaneOpen: open }),

      /** Sets whether the item modal is open */
      setItemModalOpen: (open: boolean) => set({ itemModalOpen: open }),
      setAdminItemModalOpen: (open: boolean) => set({ adminItemModalOpen: open }),

      /**
       * Toggles the favorites modal.
       * Closes the item modal if open before toggling favorites.
       */
      toggleFavoritesModal: () => {
        const { favoritesModalOpen, itemModalOpen, priceMonitorOpen } = get();

        if (itemModalOpen) {
          set({ itemModalOpen: false });
        }

        if (priceMonitorOpen) {
          set({
            priceMonitorOpen: false,
            priceMonitorItem: null,
            priceMonitorFavoriteId: null,
            priceMonitorSource: null,
          });
        }

        set({ favoritesModalOpen: !favoritesModalOpen });
      },

      /**
       * Toggles the item modal.
       * Closes the favorites modal if open before toggling item.
       */
      toggleItemModal: () => {
        const { favoritesModalOpen, itemModalOpen } = get();

        if (favoritesModalOpen) {
          set({ favoritesModalOpen: false });
        }

        set({ itemModalOpen: !itemModalOpen });
      },

      /**
       * Sets the item to show in the slide-in panel.
       *
       * @param item - The item to show, or null to close
       */
      setSlideInItem: (item: SearchResultItem | null) =>
        set((state) => {
          if (!item) {
            return { slideInItem: null };
          }

          // Selecting a different item should always dismiss the price monitor panel.
          if (state.priceMonitorOpen) {
            return {
              slideInItem: item,
              priceMonitorOpen: false,
              priceMonitorItem: null,
              priceMonitorFavoriteId: null,
              priceMonitorSource: null,
            };
          }

          return { slideInItem: item };
        }),
      openAdminItemModal: (item: SearchResultItem) =>
        set({
          adminItemModalOpen: true,
          slideInItem: item,
        }),
      closeAdminItemModal: () => set({ adminItemModalOpen: false }),

      /**
       * Sets whether user data has been loaded.
       * Use this to track initial data fetch completion.
       *
       * @param loaded - Whether user data is loaded
       */
      setUserDataLoaded: (loaded: boolean) => set({ userDataLoaded: loaded }),

      openKurboNoTokensNag: () => set({ kurboNoTokensNagOpen: true }),
      closeKurboNoTokensNag: () => set({ kurboNoTokensNagOpen: false }),

      openWarningModal: () => set({ warningModalOpen: true }),
      closeWarningModal: () => set({ warningModalOpen: false }),

      /** Resets the store to initial state */
      reset: () => set(initialState),
    },
  }))
);
